import type { ICalculationDefinition } from '@skystone/shared';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { InsightMarkdown } from '@/components/insight/InsightMarkdown';

/**
 * Renders a single `ICalculationDefinition` from `@skystone/shared`'s
 * calculation-reference module. Pure, read-only — no events, no state, no API.
 *
 * The `<h3>` anchor `concept-<conceptKey>` lets the rest of the admin page (or
 * external docs) deep-link to a specific concept.
 */
export function CalculationConceptCard({
  definition,
}: Readonly<{ definition: ICalculationDefinition }>) {
  const anchorId = `concept-${definition.conceptKey}`;
  return (
    <Card>
      <CardHeader>
        <h3 id={anchorId} className="text-lg font-semibold text-foreground">
          {definition.displayName}
        </h3>
        <p className="text-sm text-muted-foreground">{definition.shortDescription}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <InsightMarkdown content={definition.fullDescription} />
        <ConceptSourceBlock source={definition.source} />
        <ConceptScopeBlock scope={definition.scope} />
        {definition.pbiComparison ? (
          <ConceptPbiBlock comparison={definition.pbiComparison} />
        ) : null}
        {definition.caveats && definition.caveats.length > 0 ? (
          <ConceptCaveatsBlock caveats={definition.caveats} />
        ) : null}
        <ConceptConsumersLine consumers={definition.consumers} />
        {definition.repositoryMethod ? (
          <ConceptRepositoryMethodLine method={definition.repositoryMethod} />
        ) : null}
      </CardContent>
    </Card>
  );
}

function ConceptSourceBlock({
  source,
}: Readonly<{ source: ICalculationDefinition['source'] }>) {
  return (
    <section className="space-y-2">
      <h4 className="text-sm font-semibold text-foreground">Source</h4>
      <p className="text-sm text-muted-foreground">
        Primary table: <code className="rounded bg-muted px-1 py-0.5">{source.primaryTable}</code>
      </p>
      {source.joins && source.joins.length > 0 ? (
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">Joins:</p>
          <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1">
            {source.joins.map((join) => (
              <li key={join}>
                <code className="rounded bg-muted px-1 py-0.5">{join}</code>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div>
        <p className="text-sm font-medium text-foreground mb-1">SQL filter:</p>
        <pre className="rounded bg-muted px-3 py-2 text-xs overflow-x-auto whitespace-pre-wrap">
          {source.sqlFilter}
        </pre>
      </div>
    </section>
  );
}

function ConceptScopeBlock({
  scope,
}: Readonly<{ scope: ICalculationDefinition['scope'] }>) {
  return (
    <section className="space-y-2">
      <h4 className="text-sm font-semibold text-foreground">Scope</h4>
      {scope.issueTypes && scope.issueTypes.length > 0 ? (
        <p className="text-sm text-muted-foreground">
          Issue types:{' '}
          <code className="rounded bg-muted px-1 py-0.5">
            {scope.issueTypes.join(', ')}
          </code>
        </p>
      ) : null}
      <p className="text-sm text-muted-foreground">
        Archived excluded: {scope.archivedExcluded ? 'yes' : 'no'}
      </p>
      {scope.asOfDateRule ? (
        <p className="text-sm text-muted-foreground">As-of-date rule: {scope.asOfDateRule}</p>
      ) : null}
    </section>
  );
}

function ConceptPbiBlock({
  comparison,
}: Readonly<{ comparison: NonNullable<ICalculationDefinition['pbiComparison']> }>) {
  return (
    <section className="space-y-1">
      <h4 className="text-sm font-semibold text-foreground">Power BI comparison</h4>
      <p className="text-sm text-muted-foreground">
        <span className="font-medium text-foreground">Power BI:</span> {comparison.pbiBehavior}
      </p>
      <p className="text-sm text-muted-foreground">
        <span className="font-medium text-foreground">Skystone:</span>{' '}
        {comparison.skystoneBehavior}
      </p>
      <p className="text-sm text-muted-foreground">
        <span className="font-medium text-foreground">Reason:</span> {comparison.deviationReason}
      </p>
    </section>
  );
}

function ConceptCaveatsBlock({
  caveats,
}: Readonly<{ caveats: ReadonlyArray<string> }>) {
  return (
    <section className="space-y-1">
      <h4 className="text-sm font-semibold text-foreground">Caveats</h4>
      <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1">
        {caveats.map((caveat) => (
          <li key={caveat}>{caveat}</li>
        ))}
      </ul>
    </section>
  );
}

function ConceptConsumersLine({
  consumers,
}: Readonly<{ consumers: ReadonlyArray<string> }>) {
  return (
    <p className="text-sm text-muted-foreground">
      <span className="font-medium text-foreground">Consumers:</span> {consumers.join(', ')}
    </p>
  );
}

function ConceptRepositoryMethodLine({ method }: Readonly<{ method: string }>) {
  return (
    <p className="text-sm text-muted-foreground">
      <span className="font-medium text-foreground">Repository method:</span>{' '}
      <code className="rounded bg-muted px-1 py-0.5">{method}</code>
    </p>
  );
}
